/**
 * Pricing arithmetic for the billing module.
 *
 * This lives in the billing module on purpose and is the ONLY place that turns
 * a Plan row into a number shown to a user. Both the Billing page and the
 * signup wizard import from here, so a quote shown during signup and the same
 * quote shown later under Billing cannot drift apart.
 *
 * Money rules that are easy to get wrong and are centralised here:
 *
 *  - SaaS plan prices are stored PER VEHICLE, in cents.
 *  - `price_cents_annual` is the per-vehicle price PER MONTH when billed
 *    annually — NOT the yearly figure. The yearly charge is therefore
 *    price_cents_annual * quantity * 12. Reading it as a yearly price
 *    understates the bill by 12x.
 *  - AI add-ons are a flat price PER COMPANY (quantity is always 1), but they
 *    follow the SAME cycle convention as SaaS plans: `price_cents_annual` is
 *    the per-month rate when committing annually, so the yearly charge is
 *    price_cents_annual * 12. It is NOT a yearly total; reading it as one
 *    understated every annual add-on line by 12x.
 *  - Everything is computed in integer cents and rounded once, at the end.
 *    Converting to euros early and summing floats produces off-by-one-cent
 *    totals that do not match the Stripe invoice.
 */
import type { BillingCycle, Plan } from "../../api/billing";

export type Quote = {
  /** Per-vehicle price for the SaaS plan, in cents, for the chosen cycle. */
  saasPerVehicleCents: number;
  /** SaaS line total in cents for one billing period. */
  saasTotalCents: number;
  /** AI add-on line total in cents for one billing period (0 when none). */
  addonTotalCents: number;
  /** What Stripe will charge for one billing period, in cents. */
  totalCents: number;
  /** Equivalent monthly run-rate in cents — used for "≈ €X/month" hints. */
  monthlyEquivalentCents: number;
  /** Cents saved per year by choosing annual over monthly (0 if none). */
  annualSavingCents: number;
  /** How long one billing period is. */
  periodMonths: 1 | 12;
};

const cents = (plan: Pick<Plan, "price_cents_monthly" | "price_cents_annual">, cycle: BillingCycle) =>
  cycle === "annual" ? plan.price_cents_annual : plan.price_cents_monthly;

/** Per-vehicle price in cents for a SaaS plan on the given cycle. */
export const saasPerVehicleCents = (plan: Plan, cycle: BillingCycle): number =>
  cents(plan, cycle);

/**
 * SaaS line total in cents for ONE billing period.
 * Annual periods bill 12 months of the per-vehicle-per-month annual rate.
 */
export const saasTotalCents = (plan: Plan, cycle: BillingCycle, quantity: number): number => {
  const qty = Math.max(1, Math.floor(quantity || 1));
  return cycle === "annual"
    ? cents(plan, "annual") * qty * 12
    : cents(plan, "monthly") * qty;
};

/**
 * AI add-on total in cents for ONE billing period.
 *
 * Same cycle convention as SaaS plans: an annual period bills 12 months of the
 * committed annual rate. Unlike SaaS there is no quantity multiplier - the
 * add-on is a flat per-company fee, so quantity is always 1.
 */
export const addonTotalCents = (addon: Plan | null | undefined, cycle: BillingCycle): number => {
  if (!addon) return 0;
  return cycle === "annual"
    ? addon.price_cents_annual * 12
    : addon.price_cents_monthly;
};

/**
 * AI add-on price PER MONTH in cents on the given cycle.
 *
 * This is the raw stored rate - remember `price_cents_annual` is already a
 * per-month figure (the discounted rate for committing to a year), so nothing
 * is divided here. Used as the headline number on the add-on cards so they
 * read the same way as the SaaS cards above them: a monthly price up top, the
 * yearly figure underneath.
 */
export const addonPerMonthCents = (addon: Plan | null | undefined, cycle: BillingCycle): number => {
  if (!addon) return 0;
  return cents(addon, cycle);
};

/**
 * What the AI add-on costs over a FULL YEAR at the given cycle's rate.
 *
 * On annual this is the invoice total; on monthly it is twelve monthly
 * charges, which is why it is labelled "estimated" wherever it is shown. This
 * is deliberately NOT addonTotalCents: that one is a single billing period and
 * so equals the monthly rate on the monthly cycle.
 */
export const addonYearlyCents = (addon: Plan | null | undefined, cycle: BillingCycle): number =>
  addonPerMonthCents(addon, cycle) * 12;

/** Full quote for a plan + optional add-on + vehicle count. */
export const buildQuote = (
  plan: Plan | null | undefined,
  addon: Plan | null | undefined,
  cycle: BillingCycle,
  quantity: number
): Quote => {
  const periodMonths: 1 | 12 = cycle === "annual" ? 12 : 1;

  if (!plan) {
    return {
      saasPerVehicleCents: 0,
      saasTotalCents: 0,
      addonTotalCents: 0,
      totalCents: 0,
      monthlyEquivalentCents: 0,
      annualSavingCents: 0,
      periodMonths,
    };
  }

  const saas = saasTotalCents(plan, cycle, quantity);
  const add = addonTotalCents(addon, cycle);
  const total = saas + add;

  // Yearly cost on each cycle, compared like-for-like.
  const yearlyIfAnnual = saasTotalCents(plan, "annual", quantity) + addonTotalCents(addon, "annual");
  const yearlyIfMonthly = saasTotalCents(plan, "monthly", quantity) * 12 + addonTotalCents(addon, "monthly") * 12;

  return {
    saasPerVehicleCents: saasPerVehicleCents(plan, cycle),
    saasTotalCents: saas,
    addonTotalCents: add,
    totalCents: total,
    monthlyEquivalentCents: Math.round(total / periodMonths),
    annualSavingCents: Math.max(0, yearlyIfMonthly - yearlyIfAnnual),
    periodMonths,
  };
};

/**
 * Format cents as currency for display.
 *
 * `maximumFractionDigits: 0` when the amount is a whole number of euros keeps
 * headline plan prices clean (€4 rather than €4.00) while still showing cents
 * on add-ons priced at €11.08.
 */
export const formatMoney = (
  amountCents: number,
  currency = "EUR",
  locale?: string
): string => {
  const value = amountCents / 100;
  const whole = Number.isInteger(value);
  try {
    return new Intl.NumberFormat(locale || undefined, {
      style: "currency",
      currency: (currency || "EUR").toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(value);
  } catch {
    // Unknown currency code — never let a formatting failure blank the price.
    return `${whole ? value : value.toFixed(2)} ${currency}`;
  }
};

/** Convenience: is this plan the best-value / recommended one to highlight? */
export const RECOMMENDED_PLAN_SLUG = "performance";

/** Sort helper — plans are ordered by sort_order, then price. */
export const byDisplayOrder = (a: Plan, b: Plan): number =>
  (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
  a.price_cents_monthly - b.price_cents_monthly;
